import { message } from 'antd';
import proj4 from 'proj4';
import arr from './wgs84';

class CoordinatesFn {
  pi: number = 3.14159265358979;
  sm_a: number = 6378137;
  sm_b: number = 6356752.314;
  sm_EccSquared: number = 6.69437999013e-3;
  UTMScaleFactor: number = 0.9996;
  UTMZoneArray: string[] = []; // UTN zone list
  private zoneArrayDefault: string[] = [];

  constructor() {
    this.initUTMConversion();
    this.zoneArrayDefault = arr;
  }

  initUTMConversion() {
    this.UTMZoneArray = [];
    //console.log(zone);
    for (let i = 1; i <= 60; i++) {
      // this.push("SelectedZoneArray", i.toString() + "N");
      this.UTMZoneArray.push(i.toString() + 'N');
    }
    for (let i = 1; i <= 60; i++) {
      // this.push("SelectedZoneArray", i.toString() + "S");
      this.UTMZoneArray.push(i.toString() + 'S');
    }
    // this.set("SelectedZone", "");
  }

  convertLatLonToUTM(inputLng: string, inputLat: string) {
    const xy = new Array(2);

    if (isNaN(parseFloat(inputLng))) {
      return false;
    }

    const lon = parseFloat(inputLng);

    if (lon < -180.0 || 180.0 <= lon) {
      return false;
    }

    if (isNaN(parseFloat(inputLat))) {
      return false;
    }

    const lat = parseFloat(inputLat);

    if (lat < -90.0 || 90.0 < lat) {
      return false;
    }

    // Compute the UTM zone.
    let zone = Math.floor((lon + 180.0) / 6) + 1;

    zone = this.LatLonToUTMXY(this.DegToRad(lat), this.DegToRad(lon), zone, xy);
    let NS = 'N';
    if (lat < 0) {
      NS = 'S';
    }

    /* Set the output controls.  */
    // this.set('eastingValue', this.displayRoundDecimalPlaces(xy[0], 0));
    // this.set('northingValue', this.displayRoundDecimalPlaces(xy[1], 0));
    // this.set('SelectedZone', zone.toString() + NS);

    return {
      eastingValue: this.displayRoundDecimalPlaces(xy[0], 0),
      northingValue: this.displayRoundDecimalPlaces(xy[1], 0),
      SelectedZone: zone.toString() + NS,
    };
  }

  convertUTMToLatLng(
    eastingValue: string,
    northingValue: string,
    SelectedZone: string
  ) {
    const easting = parseFloat(eastingValue);
    const nothing = parseFloat(northingValue);

    if (easting < 160000 || easting > 834000) {
      message.warning('Easting value must be within [160000, 834000].');
      return;
    }
    if (nothing < 0 || nothing > 10000000) {
      message.warning('Northing value must be within [0, 10000000].');
      return;
    }
    let a = '';
    //SelectedZone
    for (let i = 0; i < this.zoneArrayDefault.length; i++) {
      if (this.zoneArrayDefault[i].indexOf('UTM zone ' + SelectedZone) > -1) {
        a = this.zoneArrayDefault[i];
        break;
      }
    }
    const b =
      'GEOGCS["WGS 84",DATUM["WGS_1984",SPHEROID["WGS 84",6378137,298.257223563,AUTHORITY["EPSG","7030"]],AUTHORITY["EPSG","6326"]],PRIMEM["Greenwich",0,AUTHORITY["EPSG","8901"]],UNIT["degree",0.0174532925199433,AUTHORITY["EPSG","9122"]],AUTHORITY["EPSG","4326"]]';

    let result = null;
    try {
      result = proj4(a, b, [easting, nothing]);
      return [+result[0].toFixed(6), +result[1].toFixed(6)];
    } catch (e) {
      console.log(e);
    }
  }

  DegToRad(deg: number) {
    return (deg / 180.0) * this.pi;
  }

  /*
   * LatLonToUTMXY
   *
   * Converts a latitude/longitude pair to x and y coordinates in the
   * Universal Transverse Mercator projection.
   *
   * Inputs:
   *   lat - Latitude of the point, in radians.
   *   lon - Longitude of the point, in radians.
   *   zone - UTM zone to be used for calculating values for x and y.
   *          If zone is less than 1 or greater than 60, the routine
   *          will determine the appropriate zone from the value of lon.
   *
   * Outputs:
   *   xy - A 2-element array where the UTM x and y values will be stored.
   *
   * Returns:
   *   The UTM zone used for calculating the values of x and y.
   *
   */
  LatLonToUTMXY(lat: number, lon: number, zone: number, xy: any[]) {
    this.MapLatLonToXY(lat, lon, this.UTMCentralMeridian(zone), xy);

    /* Adjust easting and northing for UTM system. */
    xy[0] = xy[0] * this.UTMScaleFactor + 500000.0;
    xy[1] = xy[1] * this.UTMScaleFactor;
    if (xy[1] < 0.0) xy[1] = xy[1] + 10000000.0;

    return zone;
  }

  /*
   * UTMXYToLatLon
   *
   * Converts x and y coordinates in the Universal Transverse Mercator
   * projection to a latitude/longitude pair.
   *
   * Inputs:
   *    x - The easting of the point, in meters.
   *    y - The northing of the point, in meters.
   *    zone - The UTM zone in which the point lies.
   *    southhemi - True if the point is in the southern hemisphere;
   *               false otherwise.
   *
   * Outputs:
   *    latlon - A 2-element array containing the latitude and
   *            longitude of the point, in radians.
   *
   * Returns:
   *    The function does not return a value.
   *
   */
  UTMXYToLatLon(x: number, y: number, zone: any, southhemi: any, latlon: any) {
    x -= 500000.0;
    x /= this.UTMScaleFactor;

    /* If in southern hemisphere, adjust y accordingly. */
    if (southhemi) y -= 10000000.0;

    y /= this.UTMScaleFactor;

    const cmeridian = this.UTMCentralMeridian(zone);
    this.MapXYToLatLon(x, y, cmeridian, latlon);

    return;
  }

  /*
   * UTMCentralMeridian
   *
   * Determines the central meridian for the given UTM zone.
   *
   * Inputs:
   *     zone - An integer value designating the UTM zone, range [1,60].
   *
   * Returns:
   *   The central meridian for the given UTM zone, in radians, or zero
   *   if the UTM zone parameter is outside the range [1,60].
   *   Range of the central meridian is the radian equivalent of [-177,+177].
   *
   */
  UTMCentralMeridian(zone: number) {
    const cmeridian = this.DegToRad(-183.0 + zone * 6.0);
    return cmeridian;
  }

  /*
   * MapXYToLatLon
   *
   * Converts x and y coordinates in the Transverse Mercator projection to
   * a latitude/longitude pair.  Note that Transverse Mercator is not
   * the same as UTM; a scale factor is required to convert between them.
   *
   * Reference: Hoffmann-Wellenhof, B., Lichtenegger, H., and Collins, J.,
   *   GPS: Theory and Practice, 3rd ed.  New York: Springer-Verlag Wien, 1994.
   *
   * Inputs:
   *   x - The easting of the point, in meters.
   *   y - The northing of the point, in meters.
   *   lambda0 - Longitude of the central meridian to be used, in radians.
   *
   * Outputs:
   *   philambda - A 2-element containing the latitude and longitude
   *               in radians.
   *
   * Returns:
   *   The function does not return a value.
   *
   * Remarks:
   *   The local letiables Nf, nuf2, tf, and tf2 serve the same purpose as
   *   N, nu2, t, and t2 in MapLatLonToXY, but they are computed with respect
   *   to the footpoint latitude phif.
   *
   *   x1frac, x2frac, x2poly, x3poly, etc. are to enhance readability and
   *   to optimize computations.
   *
   */
  MapXYToLatLon(x: any, y: any, lambda0: any, philambda: any) {
    let Nfpow;

    /* Get the value of phif, the footpoint latitude. */
    const phif = this.FootpointLatitude(y);

    /* Precalculate ep2 */
    const ep2 =
      (Math.pow(this.sm_a, 2.0) - Math.pow(this.sm_b, 2.0)) /
      Math.pow(this.sm_b, 2.0);

    /* Precalculate cos (phif) */
    const cf = Math.cos(phif);

    /* Precalculate nuf2 */
    const nuf2 = ep2 * Math.pow(cf, 2.0);

    /* Precalculate Nf and initialize Nfpow */
    const Nf = Math.pow(this.sm_a, 2.0) / (this.sm_b * Math.sqrt(1 + nuf2));
    Nfpow = Nf;

    /* Precalculate tf */
    const tf = Math.tan(phif);
    const tf2 = tf * tf;
    const tf4 = tf2 * tf2;

    /* Precalculate fractional coefficients for x**n in the equations
       below to simplify the expressions for latitude and longitude. */
    const x1frac = 1.0 / (Nfpow * cf);

    Nfpow *= Nf; /* now equals Nf**2) */
    const x2frac = tf / (2.0 * Nfpow);

    Nfpow *= Nf; /* now equals Nf**3) */
    const x3frac = 1.0 / (6.0 * Nfpow * cf);

    Nfpow *= Nf; /* now equals Nf**4) */
    const x4frac = tf / (24.0 * Nfpow);

    Nfpow *= Nf; /* now equals Nf**5) */
    const x5frac = 1.0 / (120.0 * Nfpow * cf);

    Nfpow *= Nf; /* now equals Nf**6) */
    const x6frac = tf / (720.0 * Nfpow);

    Nfpow *= Nf; /* now equals Nf**7) */
    const x7frac = 1.0 / (5040.0 * Nfpow * cf);

    Nfpow *= Nf; /* now equals Nf**8) */
    const x8frac = tf / (40320.0 * Nfpow);

    /* Precalculate polynomial coefficients for x**n.
       -- x**1 does not have a polynomial coefficient. */
    const x2poly = -1.0 - nuf2;

    const x3poly = -1.0 - 2 * tf2 - nuf2;

    const x4poly =
      5.0 +
      3.0 * tf2 +
      6.0 * nuf2 -
      6.0 * tf2 * nuf2 -
      3.0 * (nuf2 * nuf2) -
      9.0 * tf2 * (nuf2 * nuf2);

    const x5poly =
      5.0 + 28.0 * tf2 + 24.0 * tf4 + 6.0 * nuf2 + 8.0 * tf2 * nuf2;

    const x6poly =
      -61.0 - 90.0 * tf2 - 45.0 * tf4 - 107.0 * nuf2 + 162.0 * tf2 * nuf2;

    const x7poly = -61.0 - 662.0 * tf2 - 1320.0 * tf4 - 720.0 * (tf4 * tf2);

    const x8poly = 1385.0 + 3633.0 * tf2 + 4095.0 * tf4 + 1575 * (tf4 * tf2);

    /* Calculate latitude */
    philambda[0] =
      phif +
      x2frac * x2poly * (x * x) +
      x4frac * x4poly * Math.pow(x, 4.0) +
      x6frac * x6poly * Math.pow(x, 6.0) +
      x8frac * x8poly * Math.pow(x, 8.0);

    /* Calculate longitude */
    philambda[1] =
      lambda0 +
      x1frac * x +
      x3frac * x3poly * Math.pow(x, 3.0) +
      x5frac * x5poly * Math.pow(x, 5.0) +
      x7frac * x7poly * Math.pow(x, 7.0);

    return;
  }

  /*
   * FootpointLatitude
   *
   * Computes the footpoint latitude for use in converting transverse
   * Mercator coordinates to ellipsoidal coordinates.
   *
   * Reference: Hoffmann-Wellenhof, B., Lichtenegger, H., and Collins, J.,
   *   GPS: Theory and Practice, 3rd ed.  New York: Springer-Verlag Wien, 1994.
   *
   * Inputs:
   *   y - The UTM northing coordinate, in meters.
   *
   * Returns:
   *   The footpoint latitude, in radians.
   *
   */
  FootpointLatitude(y: number) {
    /* Precalculate n (Eq. 10.18) */
    const n = (this.sm_a - this.sm_b) / (this.sm_a + this.sm_b);

    /* Precalculate alpha_ (Eq. 10.22) */
    /* (Same as alpha in Eq. 10.17) */
    const alpha_ =
      ((this.sm_a + this.sm_b) / 2.0) *
      (1 + Math.pow(n, 2.0) / 4 + Math.pow(n, 4.0) / 64);

    /* Precalculate y_ (Eq. 10.23) */
    const y_ = y / alpha_;

    /* Precalculate beta_ (Eq. 10.22) */
    const beta_ =
      (3.0 * n) / 2.0 +
      (-27.0 * Math.pow(n, 3.0)) / 32.0 +
      (269.0 * Math.pow(n, 5.0)) / 512.0;

    /* Precalculate gamma_ (Eq. 10.22) */
    const gamma_ =
      (21.0 * Math.pow(n, 2.0)) / 16.0 + (-55.0 * Math.pow(n, 4.0)) / 32.0;

    /* Precalculate delta_ (Eq. 10.22) */
    const delta_ =
      (151.0 * Math.pow(n, 3.0)) / 96.0 + (-417.0 * Math.pow(n, 5.0)) / 128.0;

    /* Precalculate epsilon_ (Eq. 10.22) */
    const epsilon_ = (1097.0 * Math.pow(n, 4.0)) / 512.0;

    /* Now calculate the sum of the series (Eq. 10.21) */
    const result =
      y_ +
      beta_ * Math.sin(2.0 * y_) +
      gamma_ * Math.sin(4.0 * y_) +
      delta_ * Math.sin(6.0 * y_) +
      epsilon_ * Math.sin(8.0 * y_);

    return result;
  }

  displayRoundDecimalPlaces(numb: any, digit: number) {
    const factor = Math.pow(10, digit);
    return (Math.round(Number(numb) * factor) / factor).toFixed(digit);
  }

  /*
   * MapLatLonToXY
   *
   * Converts a latitude/longitude pair to x and y coordinates in the
   * Transverse Mercator projection.  Note that Transverse Mercator is not
   * the same as UTM; a scale factor is required to convert between them.
   *
   * Reference: Hoffmann-Wellenhof, B., Lichtenegger, H., and Collins, J.,
   * GPS: Theory and Practice, 3rd ed.  New York: Springer-Verlag Wien, 1994.
   *
   * Inputs:
   *    phi - Latitude of the point, in radians.
   *    lambda - Longitude of the point, in radians.
   *    lambda0 - Longitude of the central meridian to be used, in radians.
   *
   * Outputs:
   *    xy - A 2-element array containing the x and y coordinates
   *         of the computed point.
   *
   * Returns:
   *    The function does not return a value.
   *
   */
  MapLatLonToXY(
    phi: number,
    lambda: number,
    lambda0: number,
    xy: Array<number>
  ) {
    /* Precalculate ep2 */
    const ep2 =
      (Math.pow(this.sm_a, 2.0) - Math.pow(this.sm_b, 2.0)) /
      Math.pow(this.sm_b, 2.0);

    /* Precalculate nu2 */
    const nu2 = ep2 * Math.pow(Math.cos(phi), 2.0);

    /* Precalculate N */
    const N = Math.pow(this.sm_a, 2.0) / (this.sm_b * Math.sqrt(1 + nu2));

    /* Precalculate t */
    const t = Math.tan(phi);
    const t2 = t * t;
    // tmp = t2 * t2 * t2 - Math.pow(t, 6.0);

    /* Precalculate l */
    const l = lambda - lambda0;

    /* Precalculate coefficients for l**n in the equations below
       so a normal human being can read the expressions for easting
       and northing
       -- l**1 and l**2 have coefficients of 1.0 */
    const l3coef = 1.0 - t2 + nu2;

    const l4coef = 5.0 - t2 + 9 * nu2 + 4.0 * (nu2 * nu2);

    const l5coef = 5.0 - 18.0 * t2 + t2 * t2 + 14.0 * nu2 - 58.0 * t2 * nu2;

    const l6coef = 61.0 - 58.0 * t2 + t2 * t2 + 270.0 * nu2 - 330.0 * t2 * nu2;

    const l7coef = 61.0 - 479.0 * t2 + 179.0 * (t2 * t2) - t2 * t2 * t2;

    const l8coef = 1385.0 - 3111.0 * t2 + 543.0 * (t2 * t2) - t2 * t2 * t2;

    /* Calculate easting (x) */

    xy[0] =
      N * Math.cos(phi) * l +
      (N / 6.0) * Math.pow(Math.cos(phi), 3.0) * l3coef * Math.pow(l, 3.0) +
      (N / 120.0) * Math.pow(Math.cos(phi), 5.0) * l5coef * Math.pow(l, 5.0) +
      (N / 5040.0) * Math.pow(Math.cos(phi), 7.0) * l7coef * Math.pow(l, 7.0);

    /* Calculate northing (y) */
    xy[1] =
      this.ArcLengthOfMeridian(phi) +
      (t / 2.0) * N * Math.pow(Math.cos(phi), 2.0) * Math.pow(l, 2.0) +
      (t / 24.0) *
        N *
        Math.pow(Math.cos(phi), 4.0) *
        l4coef *
        Math.pow(l, 4.0) +
      (t / 720.0) *
        N *
        Math.pow(Math.cos(phi), 6.0) *
        l6coef *
        Math.pow(l, 6.0) +
      (t / 40320.0) *
        N *
        Math.pow(Math.cos(phi), 8.0) *
        l8coef *
        Math.pow(l, 8.0);

    return;
  }

  /*
   * ArcLengthOfMeridian
   *
   * Computes the ellipsoidal distance from the equator to a point at a
   * given latitude.
   *
   * Reference: Hoffmann-Wellenhof, B., Lichtenegger, H., and Collins, J.,
   * GPS: Theory and Practice, 3rd ed.  New York: Springer-Verlag Wien, 1994.
   *
   * Inputs:
   *     phi - Latitude of the point, in radians.
   *
   * Globals:
   *     sm_a - Ellipsoid model major axis.
   *     sm_b - Ellipsoid model minor axis.
   *
   * Returns:
   *     The ellipsoidal distance of the point from the equator, in meters.
   *
   */
  ArcLengthOfMeridian(phi: number) {
    /* Precalculate n */
    const n = (this.sm_a - this.sm_b) / (this.sm_a + this.sm_b);

    /* Precalculate alpha */
    const alpha =
      ((this.sm_a + this.sm_b) / 2.0) *
      (1.0 + Math.pow(n, 2.0) / 4.0 + Math.pow(n, 4.0) / 64.0);

    /* Precalculate beta */
    const beta =
      (-3.0 * n) / 2.0 +
      (9.0 * Math.pow(n, 3.0)) / 16.0 +
      (-3.0 * Math.pow(n, 5.0)) / 32.0;

    /* Precalculate gamma */
    const gamma =
      (15.0 * Math.pow(n, 2.0)) / 16.0 + (-15.0 * Math.pow(n, 4.0)) / 32.0;

    /* Precalculate delta */
    const delta =
      (-35.0 * Math.pow(n, 3.0)) / 48.0 + (105.0 * Math.pow(n, 5.0)) / 256.0;

    /* Precalculate epsilon */
    const epsilon = (315.0 * Math.pow(n, 4.0)) / 512.0;

    /* Now calculate the sum of the series and return */
    const result =
      alpha *
      (phi +
        beta * Math.sin(2.0 * phi) +
        gamma * Math.sin(4.0 * phi) +
        delta * Math.sin(6.0 * phi) +
        epsilon * Math.sin(8.0 * phi));

    return result;
  }
}

export default new CoordinatesFn();

export function SplitCoordinates(coords: string) {
  const array = coords.split('_');
  return { lng: array[0], lat: array[1] };
}
